import React from 'react';
import { Typography, Skeleton, Card } from 'antd';
import { UserOutlined, CalendarOutlined, PhoneOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import styles from '../Profile.module.css';

const { Title } = Typography;

interface PersonalInfoSectionProps {
  gender?: string;
  dateOfBirth?: string;
  phoneCode?: string;
  isLoading: boolean;
}

const PersonalInfoSection: React.FC<PersonalInfoSectionProps> = ({
  gender,
  dateOfBirth,
  phoneCode,
  isLoading
}) => {
  const { t } = useTranslation();

  // Don't render if no data and not loading
  if (!isLoading && !gender && !dateOfBirth && !phoneCode) {
    return null;
  }

  const formatDateOfBirth = (dateString?: string) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatGender = (genderValue?: string) => {
    if (!genderValue) return null;
    return genderValue.charAt(0).toUpperCase() + genderValue.slice(1).toLowerCase();
  };

  const formatPhoneCode = (phoneCodeValue?: string) => {
    if (!phoneCodeValue) return null;
    return phoneCodeValue.startsWith('+') ? phoneCodeValue : `+${phoneCodeValue}`;
  };

  return (
    <Card className={styles.sectionCard} bordered={false}>
      <Title level={4} className={styles.sectionTitle}>
        {t('#Profile.personal_info')}
      </Title>

      <div className={styles.personalInfoGrid}>
        {/* Gender */}
        {(gender || isLoading) && (
          <div className={styles.personalInfoCard}>
            <Skeleton loading={isLoading} paragraph={false} active>
              <div className={styles.personalInfoContent}>
                <div className={styles.personalInfoIcon}>
                  <UserOutlined />
                </div>
                <div className={styles.personalInfoDetails}>
                  <div className={styles.personalInfoLabel}>
                    {t('#Profile.gender')}
                  </div>
                  <div className={styles.personalInfoValue}>
                    {formatGender(gender) || '—'}
                  </div>
                </div>
              </div>
            </Skeleton>
          </div>
        )}

        {/* Date of Birth */}
        {(dateOfBirth || isLoading) && (
          <div className={styles.personalInfoCard}>
            <Skeleton loading={isLoading} paragraph={false} active>
              <div className={styles.personalInfoContent}>
                <div className={styles.personalInfoIcon}>
                  <CalendarOutlined />
                </div>
                <div className={styles.personalInfoDetails}>
                  <div className={styles.personalInfoLabel}>
                    {t('#Profile.date_of_birth')}
                  </div>
                  <div className={styles.personalInfoValue}>
                    {formatDateOfBirth(dateOfBirth) || '—'}
                  </div>
                </div>
              </div>
            </Skeleton>
          </div>
        )}

        {/* Phone Code */}
        {(phoneCode || isLoading) && (
          <div className={styles.personalInfoCard}>
            <Skeleton loading={isLoading} paragraph={false} active>
              <div className={styles.personalInfoContent}>
                <div className={styles.personalInfoIcon}>
                  <PhoneOutlined />
                </div>
                <div className={styles.personalInfoDetails}>
                  <div className={styles.personalInfoLabel}>
                    {t('#Profile.phone_code')}
                  </div>
                  <div className={styles.personalInfoValue}>
                    {formatPhoneCode(phoneCode) || '—'}
                  </div>
                </div>
              </div>
            </Skeleton>
          </div>
        )}
      </div>
    </Card>
  );
};

export default PersonalInfoSection;